import { z } from 'zod';

import { SubmissionDeadlineDto } from './admin-reporting-periods.js';
import { TimesheetPeriod } from './timesheets.js';

// Local, matching admin-users.ts and admin-reporting-periods.ts, which each keep
// their own copy rather than sharing one.
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const AnomalyItem = z.object({
  kind: z.enum(['missing', 'under', 'overtime']),
  severity: z.enum(['warn', 'info']),
  label: z.string(),
});
export type AnomalyItem = z.infer<typeof AnomalyItem>;

export const ApprovalDecisionItem = z.object({
  decisionId: z.string().uuid(),
  actorUserId: z.string().uuid(),
  actorDisplayName: z.string(),
  decision: z.enum(['approved', 'returned', 'unapproved']),
  comment: z.string().nullable(),
  decidedAt: z.string(),
});
export type ApprovalDecisionItem = z.infer<typeof ApprovalDecisionItem>;

export const BreakdownItem = z.object({
  lineId: z.string().uuid(),
  label: z.string(),
  projectCode: z.string().nullable(),
  isWorkTime: z.boolean(),
  totalMinutes: z.number().int().nonnegative(),
  // Contributor's free-text line note (timesheet_lines.description), surfaced
  // read-only to approvers. Null when the line has no note.
  note: z.string().nullable(),
});
export type BreakdownItem = z.infer<typeof BreakdownItem>;

export const ApprovalInboxEntry = z.object({
  userId: z.string().uuid(),
  name: z.string(),
  initials: z.string(),
  // Human-readable UT EID, shown as the leading identifier in the drawer header
  // and inbox rows. Null for users with no EID on record (e.g. pre-AD-sync rows).
  utEid: z.string().nullable().default(null),
  // Whether this person is still active. An inactive person is kept in the inbox
  // only when they still have a sheet an approver can act on (submitted or
  // returned) — dropping them outright would strand hours that reach the
  // cost-center export by no other route, since approval is the only thing that
  // publishes (M-04). The UI marks such a row rather than hiding it, which is the
  // same call docs/solutions/logic-errors/admin-lines-disappear-and-ghost-draft-on-assignment-deactivation-2026-05-17.md
  // made one level down, for timesheet lines.
  activeFlag: z.boolean().default(true),
  timesheetId: z.string().uuid().nullable(),
  status: z.enum(['draft', 'submitted', 'approved', 'returned', 'missing']),
  submittedAt: z.string().nullable(),
  // Contributor's sheet-level comment attached on submit (timesheets.comment),
  // surfaced read-only to approvers. Null when none.
  submitComment: z.string().nullable().default(null),
  totalMinutes: z.number().int().nonnegative(),
  targetMinutes: z.number().int().nonnegative(),
  avgPrev4Minutes: z.number().int().nonnegative().nullable(),
  breakdown: z.array(BreakdownItem),
  anomalies: z.array(AnomalyItem),
  lastDecision: ApprovalDecisionItem.nullable(),
  teamId: z.string().uuid().nullable().default(null),
  teamName: z.string().nullable().default(null),
  teamLeadUserId: z.string().uuid().nullable().default(null),
  teamLeadDisplayName: z.string().nullable().default(null),
});
export type ApprovalInboxEntry = z.infer<typeof ApprovalInboxEntry>;

export const ApprovalInboxResponse = z.object({
  period: TimesheetPeriod,
  // True when a closed reporting period owns this week. Approve, return and
  // unapprove are all refused for it (REPORTING_PERIOD_CLOSED), so the week view
  // withholds those actions and says why instead of offering a button that will
  // fail. Same name and rule as `TimesheetResponse.isLocked`. The rows themselves
  // are still listed — lines never disappear.
  isLocked: z.boolean(),
  entries: z.array(ApprovalInboxEntry),
  approverTeamIds: z.array(z.string().uuid()).optional(),
});
export type ApprovalInboxResponse = z.infer<typeof ApprovalInboxResponse>;

export const ApproveRequest = z.object({
  idempotencyKey: z.string().uuid(),
});
export type ApproveRequest = z.infer<typeof ApproveRequest>;

export const ReturnRequest = z.object({
  comment: z.string().trim().min(1, 'Comment is required'),
  idempotencyKey: z.string().uuid(),
});
export type ReturnRequest = z.infer<typeof ReturnRequest>;

export const UnapproveRequest = z.object({
  comment: z.string().trim().min(1, 'Comment is required'),
  idempotencyKey: z.string().uuid(),
});
export type UnapproveRequest = z.infer<typeof UnapproveRequest>;

/**
 * Upper bound on one bulk-approve request.
 *
 * `bulkApproveTimesheets` is a sequential per-item loop — idempotency probe, auth
 * check, closed-period check, then a transaction — and the API Lambda is capped
 * at 29 seconds. The single-week inbox bounded this implicitly at one team's
 * roster; the cross-week triage view does not, so an unbounded request could time
 * out mid-loop with some sheets already approved and the client told nothing was.
 * The client chunks anything larger and merges the per-chunk results.
 *
 * 200 matches the pagination ceiling `get-history` already uses. It is a
 * deliberately conservative starting point, not a measured optimum.
 */
export const BULK_APPROVE_MAX_ITEMS = 200;

export const BulkApproveRequest = z.object({
  timesheetIds: z
    .array(z.string().uuid())
    .min(1, 'At least one timesheet ID is required')
    .max(BULK_APPROVE_MAX_ITEMS, `At most ${BULK_APPROVE_MAX_ITEMS} timesheets per request`)
    // Distinct, because the per-item idempotency key is derived from the timesheet id:
    // a repeated id makes its second occurrence look like a replay of its own first
    // one, which the loop reports as another success. `approved` then carries the id
    // twice and the toast claims more sheets changed than exist. The UI cannot produce
    // a duplicate — selection is a Set keyed one-to-one with a timesheet — so this
    // guards the contract itself rather than a reachable button.
    .refine((ids) => new Set(ids).size === ids.length, {
      message: 'timesheetIds must not contain duplicates',
    }),
  idempotencyKey: z.string().uuid(),
});
export type BulkApproveRequest = z.infer<typeof BulkApproveRequest>;

/**
 * Why one item in a bulk approve did not get approved.
 *
 * A named union rather than free text so the UI can tell a benign outcome from a
 * real failure — "a co-worker already handled it" reads very differently from
 * "that period is closed" — without matching on message strings.
 *
 * `not_submitted` is deliberately absent: the `skipped` array already carries
 * that meaning, and the loop pushes to it before any reason is recorded.
 */
export const BulkApproveFailureReason = z.enum([
  'locked_period',
  'forbidden',
  'not_found',
  'idempotency_conflict',
  // Approving one of your own sheets individually is allowed; sweeping it up in a
  // bulk action is not. Named rather than folded into `forbidden` because the UI
  // should say why — it is a segregation-of-duties rule, not a permission failure.
  'self_approval',
  // An administrator was closing or reopening this week's reporting period at the same
  // moment, and the row lock timed out. Distinct from `locked_period`, which is final,
  // and from `unexpected`, which offers no remedy: this one simply needs sending again.
  'contended',
  'unexpected',
]);
export type BulkApproveFailureReason = z.infer<typeof BulkApproveFailureReason>;

export const BulkApproveResponse = z.object({
  approved: z.array(z.string().uuid()),
  // Reserved exclusively for "was not in submitted status" — someone else got
  // there first. Benign; the row simply refreshes out of the list.
  skipped: z.array(z.string().uuid()),
  errors: z.array(z.object({ id: z.string().uuid(), reason: BulkApproveFailureReason })),
});
export type BulkApproveResponse = z.infer<typeof BulkApproveResponse>;

export const ApprovalActionResponse = z.object({
  timesheetId: z.string().uuid(),
  status: z.string(),
  decision: ApprovalDecisionItem,
});
export type ApprovalActionResponse = z.infer<typeof ApprovalActionResponse>;

export const ReportingPeriodsResponse = z.object({
  periods: z.array(TimesheetPeriod),
});
export type ReportingPeriodsResponse = z.infer<typeof ReportingPeriodsResponse>;

// ---- Cross-week "Needs action" triage ---------------------------------------

/**
 * What the approver can do with a row.
 *
 * `approve` is the only actionable kind. `nudge` (never submitted, or still a
 * draft) and `waiting` (returned and not resubmitted) are listed for visibility
 * with a label naming who they wait on — deliberately not a Remind button, since
 * automated reminders are deferred (U-08) and a control that does nothing is
 * worse than none.
 */
export const OutstandingItemKind = z.enum(['approve', 'nudge', 'waiting']);
export type OutstandingItemKind = z.infer<typeof OutstandingItemKind>;

/**
 * Deliberately NOT `OutstandingWeekStatus` from the contributor side, which
 * narrows to draft/returned/missing. `submitted` is the whole point of this view
 * — it is the only status an approver can act on — and reusing the contributor
 * union here would render a triage list with zero approvable rows.
 */
export const OutstandingItemStatus = z.enum(['draft', 'submitted', 'returned', 'missing']);
export type OutstandingItemStatus = z.infer<typeof OutstandingItemStatus>;

/** Wire vocabulary for the filter pills. Server-side, so a pill's count and its
 * rows always describe the same set even once the row cap binds. */
export const OutstandingApprovalsFilter = z.enum([
  'everything',
  'needs_approval',
  'not_submitted',
  'waiting_on_fix',
  'past_due',
]);
export type OutstandingApprovalsFilter = z.infer<typeof OutstandingApprovalsFilter>;

export const OutstandingApprovalItem = z.object({
  /**
   * `${userId}@${weekStartDate}`. Composite because a person appears once per
   * outstanding week, and keyed on ids rather than a display name: two people
   * sharing a name would otherwise collide in a Map and silently drop rows —
   * docs/solutions/ui-bugs/timesheet-grid-groups-by-non-unique-project-name-2026-05-10.md.
   */
  key: z.string(),
  userId: z.string().uuid(),
  name: z.string(),
  initials: z.string(),
  utEid: z.string().nullable().default(null),
  activeFlag: z.boolean().default(true),
  timesheetId: z.string().uuid().nullable(),

  weekStartDate: z.string().regex(DATE_REGEX),
  weekEndDate: z.string().regex(DATE_REGEX),
  /** Absolute instant, carrying its offset. The browser never derives lateness. */
  dueAt: z.string(),
  /** Whole calendar days past the deadline date. Zero on it, negative before it. */
  daysPastDue: z.number().int(),
  isCurrentWeek: z.boolean(),

  status: OutstandingItemStatus,
  kind: OutstandingItemKind,
  totalMinutes: z.number().int().nonnegative(),
  targetMinutes: z.number().int().nonnegative(),
  /** Empty for a clean sheet, which is what the bulk "approve clean" acts on. */
  anomalies: z.array(AnomalyItem),

  /** The single team this item is attributed to, so group roll-ups sum to the hero total. */
  teamId: z.string().uuid().nullable().default(null),
  teamName: z.string().nullable().default(null),
  teamLeadUserId: z.string().uuid().nullable().default(null),
  teamLeadDisplayName: z.string().nullable().default(null),
  /** Every one of the viewer's teams this person belongs to, for Team grouping. */
  teamIds: z.array(z.string().uuid()).default([]),

  isSelf: z.boolean().default(false),
  /** Admin acting on another team's sheet: approval happens in the drawer. */
  aobo: z.boolean().default(false),
  /**
   * Directly approvable by this viewer — drives checkbox rendering and select-all.
   * Excludes the viewer's OWN row: approving your own sheet individually is allowed,
   * but no bulk path may sweep it up, and the server refuses it with `self_approval`.
   */
  selectable: z.boolean().default(false),
});
export type OutstandingApprovalItem = z.infer<typeof OutstandingApprovalItem>;

export const OutstandingApprovalCounts = z.object({
  all: z.number().int().nonnegative(),
  approve: z.number().int().nonnegative(),
  nudge: z.number().int().nonnegative(),
  waiting: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
});
export type OutstandingApprovalCounts = z.infer<typeof OutstandingApprovalCounts>;

export const OutstandingApprovalsResponse = z.object({
  /** Capped. Never derive a count from this array — see `counts`. */
  items: z.array(OutstandingApprovalItem),
  /**
   * Uncapped, over the filtered set. Drives the filter pills, which would
   * otherwise disagree with what clicking them shows once the cap binds.
   */
  counts: OutstandingApprovalCounts,
  /**
   * Uncapped, and narrower than `counts.all`: excludes rows that are both not yet
   * due and not yet submitted. U-01 auto-creates every contributor's current week
   * as a draft, so counting those would leave the tab badge permanently lit and
   * the all-clear state unreachable. Drives the badge and the hero total.
   */
  awaitingActionCount: z.number().int().nonnegative(),
  /**
   * The instant `awaitingActionCount` next changes, or null when nothing is waiting on a
   * deadline.
   *
   * Server-computed, over the uncapped set, because the count it predicts is uncapped
   * too. A client cannot derive it from `items`: those are truncated most-overdue-first,
   * and a not-yet-due row is by definition the least overdue thing in the list, so it is
   * the first to be cut. Deriving it there meant a large backlog scheduled no refresh at
   * all and the badge stayed at its pre-deadline value until midnight.
   */
  nextAwaitingActionAt: z.string().datetime().nullable(),
  /**
   * Distinct weeks, and the worst lateness, across the WHOLE backlog — unfiltered,
   * unsearched and uncapped, like `awaitingActionCount`. The hero is an overview, so
   * deriving these client-side from `items` made them move with every filter click and
   * silently understate the oldest item once the row cap bound.
   */
  weeksRepresented: z.number().int().nonnegative(),
  oldestDaysPastDue: z.number().int(),
  /** True when `items` was truncated, so the UI can say so rather than mislead. */
  capped: z.boolean(),
  /**
   * People in scope, before any week was considered.
   *
   * Zero and "all caught up" are the same empty list otherwise, and they need
   * opposite copy: one says the backlog is clear, the other says you approve for
   * nobody and should ask an administrator. Without this the second renders as
   * the first, quietly telling someone there is no work when in fact they cannot
   * see any.
   */
  scopeUserCount: z.number().int().nonnegative(),
  /** The rule `dueAt` was computed against, so the footnote can state it. */
  deadline: SubmissionDeadlineDto,
});
export type OutstandingApprovalsResponse = z.infer<typeof OutstandingApprovalsResponse>;

/** Drawer payload for one (person, week) — a full inbox entry plus its week context. */
export const OutstandingApprovalItemDetail = z.object({
  entry: ApprovalInboxEntry,
  weekStartDate: z.string().regex(DATE_REGEX),
  weekEndDate: z.string().regex(DATE_REGEX),
  dueAt: z.string(),
  daysPastDue: z.number().int(),
  isCurrentWeek: z.boolean(),
});
export type OutstandingApprovalItemDetail = z.infer<typeof OutstandingApprovalItemDetail>;
